const { debounce } = window.BAO.Utils

class QuickSearch extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.countAbortController = null
    this.currentSearchTerm = ''
  }

  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.loadResults = this.loadResults.bind(this)
    this.setupResults = this.setupResults.bind(this)
    this.onEmptyInput = this.onEmptyInput.bind(this)
    this.onNoResults = this.onNoResults.bind(this)

    this.onPerformSearch = debounce(this.onPerformSearch.bind(this), 300)
    this.onClearSearch = this.onClearSearch.bind(this)
    this.loadCount = debounce(this.loadCount.bind(this), 500)

    if (this.els.input?.element) {
      this.listeners.add(this.els.input.element, 'keyup', this.onPerformSearch)
      this.listeners.add(this.els.input.element, 'focus', () => {
        this.setAttribute('aria-hidden', 'false')
      })
    }

    if (this.els.clear?.element) {
      this.listeners.add(this.els.clear.element, 'click', this.onClearSearch)
    }
  }

  onClearSearch () {
    if (this.els.input?.element) {
      this.els.input.element.value = ''
      this.els.input.element.focus()
    }
    this.onEmptyInput()
  }

  onPerformSearch (event) {
    const searchTerm = event.target.value

    if (searchTerm.length < 3) {
      this.onEmptyInput()
    } else {
      this.loadResults(searchTerm)
    }
  }

  onEmptyInput () {
    this.els['no-results']?.element?.setAttribute('aria-hidden', 'true')

    this.els.layout?.elements?.forEach((layoutEl) => {
      layoutEl.setAttribute('data-layout', 'default')
      layoutEl.setAttribute('aria-hidden', 'false')
    })

    this.els['suggestions-container']?.element?.setAttribute(
      'aria-hidden',
      'true',
    )

    this.els['default-suggestion']?.elements?.forEach((defaultSuggestionEl) => {
      defaultSuggestionEl.setAttribute('aria-hidden', 'false')
    })

    this.els['results-main']?.element?.setAttribute('aria-hidden', 'true')
    this.els['default-main']?.element?.setAttribute('aria-hidden', 'false')
  }

  onNoResults () {
    const searchTerm = this.els.input?.element?.value || ''

    if (this.els.query?.element) {
      this.els.query.element.innerText = searchTerm
    }

    this.els.layout?.elements?.forEach((layoutEl) => {
      layoutEl.setAttribute('aria-hidden', 'true')
    })

    this.els['no-results']?.element?.setAttribute('aria-hidden', 'false')
  }

  async loadResults (searchTerm) {
    if (this.countAbortController) {
      this.countAbortController.abort()
      this.countAbortController = null
    }

    if (this.els.count?.element) {
      this.els.count.element.textContent = ''
    }

    this.currentSearchTerm = searchTerm

    const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,query&resources[limit]=10&section_id=predictive-search`

    try {
      const data = await window.BAO.Utils.fetchJSON(searchUrl, { method: 'GET' })

      const products = data.resources?.results?.products || []
      const collections = data.resources?.results?.collections || []
      const queries = data.resources?.results?.queries || []

      if (products.length > 1) {
        this.setupResults(products, searchTerm, null)
      } else if (products.length == 1) {
        if (products[0].tags.includes('sale-hide')) {
          this.onNoResults()
        } else {
          this.setupResults(products, searchTerm, null)
        }
      } else {
        this.onNoResults()
      }

      this.setupSuggestions(queries, collections)

      this.loadCount(searchTerm)
    } catch (error) {
      this.onNoResults()
    }
  }

  async loadCount (searchTerm) {
    if (this.countAbortController) {
      this.countAbortController.abort()
    }

    this.countAbortController = new AbortController()

    try {
      const searchPageUrl = `${window.Shopify.routes.root}search?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`
      const sectionHTML = await window.BAO.sectionRenderer.fetchSingle('search-count', searchPageUrl)

      if (sectionHTML && this.els.count?.element && this.currentSearchTerm === searchTerm) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = sectionHTML
        const countElement = tempDiv.querySelector('#search-results-count')

        if (countElement) {
          const count = parseInt(countElement.textContent?.trim(), 10)
          if (!isNaN(count)) {
            this.els.count.element.textContent = `(${count})`
          }
        }
      }
    } catch (countError) {
      if (countError.name !== 'AbortError') {
      }
    } finally {
      this.countAbortController = null
    }
  }

  async setupSuggestions (queries, collections) {
    const queriesHTML =
      queries?.length > 0
        ? `<div class="sec-QuickSearch_SuggestionGroup"><div class="qs-MobileSearch_SuggestionResults">${this.getQueriesHTML(queries)}</div></div>`
        : ''

    const collectionsHTML =
      collections?.length > 0
        ? `<div class="sec-QuickSearch_SuggestionGroup">
          <h3 class="qs-MobileSearch_HeaderTitle fz-16_100">
            ${window.quickSearchStrings.collections}
          </h3>
          <div class="qs-MobileSearch_SuggestionResults">
            ${this.getCollectionsHTML(collections)}
          </div>
        </div>`
        : ''

    if (this.els.suggestions?.element) {
      this.els.suggestions.element.innerHTML = `<div class="sec-QuickSearch_SuggestionGroups">${queriesHTML}${collectionsHTML}</div>`
    }

    const hasAnyRecords = queries.length > 0 || collections.length > 0

    this.els['suggestions-container']?.element?.setAttribute(
      'aria-hidden',
      hasAnyRecords ? 'false' : 'true',
    )

    this.els['default-suggestion']?.elements?.forEach((defaultSuggestionEl) => {
      defaultSuggestionEl.setAttribute(
        'aria-hidden',
        hasAnyRecords ? 'true' : 'false',
      )
    })
  }

  getCollectionsHTML (collections) {
    if (!collections || collections.length === 0) return ''

    return collections
      .map((collection) => {
        return `
          <a href="${collection.url}" class="qs-MobileSearch_SuggestionButton fz-14_160-constant">
            ${collection.title}
          </a>
        `
      })
      .join('')
  }

  getQueriesHTML (queries) {
    if (!queries || queries.length === 0) return ''

    return queries
      .map((query) => {
        return `
          <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(query.text)}" class="qs-MobileSearch_SuggestionButton fz-14_160-constant">
            ${query.styled_text || query.text}
          </a>
        `
      })
      .join('')
  }

  async setupResults (products, searchTerm, totalCount) {
    if (products.length < 1) {
      this.onNoResults()
      return
    } else {
      this.els.layout?.elements?.forEach((layoutEl) => {
        layoutEl.setAttribute('aria-hidden', 'false')
      })

      this.els['no-results']?.element?.setAttribute('aria-hidden', 'true')
    }

    this.setAttribute('aria-busy', 'true')

    const limitedProducts = products.slice(0, 4)
    const productCardHTML = await this.getAllProductCardHTML(limitedProducts)

    if (this.els.results?.element) {
      this.els.results.element.innerHTML = productCardHTML
    }

    if (this.els.count?.element && totalCount !== null) {
      this.els.count.element.textContent = `(${totalCount})`
    }

    const viewAllHTML = `<a href='${window.Shopify.routes.root}search?q=${encodeURIComponent(searchTerm)}' class="qs-MobileSearch_ViewAll fz-12_100">
      ${window.quickSearchStrings.viewAllResults}
    </a>`

    if (this.els['view-all']?.element) {
      this.els['view-all'].element.innerHTML = viewAllHTML
    }

    this.els['results-main']?.element?.setAttribute(
      'aria-hidden',
      products.length < 1 ? 'true' : 'false',
    )

    this.els['default-main']?.element?.setAttribute(
      'aria-hidden',
      products.length > 0 ? 'true' : 'false',
    )

    this.els.layout?.elements?.forEach((layoutEl) => {
      layoutEl.setAttribute('data-layout', 'results')
    })

    this.setAttribute('aria-busy', 'false')
  }

  async getAllProductCardHTML (products) {
    const productCardHTML = await Promise.all(
      products.map(async (product) => await this.getProductCardHTML(product)),
    )
    const filtered = productCardHTML.filter(Boolean)
    return filtered.join('')
  }

  async getProductCardHTML (product) {
    const productUrl = product.url

    if (product.tags.includes('sale-hide')) {
      return
    }

    return window.BAO.sectionRenderer
      .fetchSingle('product-card-search', `${productUrl}`)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('.prd-SearchCard')

        const sale_hide_status = myself.hasAttribute('data-sale-hide')

        if (myself) {
          return `<div class="sec-QuickSearch_ProductCard">${myself.outerHTML}</div>`
        } else {
          return null
        }
      })
      .catch(() => {
        return null
      })
  }
}

if (!customElements.get('quick-search')) {
  customElements.define('quick-search', QuickSearch)
}
